/**
 * promotionReadiness.ts
 * Typed contract and pure validator for trade setup draft promotion readiness.
 *
 * Defines the finite set of required planning fields that must be present on a
 * TradeSetupDraft before promotion to a persistent Trade is permitted, and the
 * result shape returned by the validator.
 *
 * All functions in this module are intentionally pure — no DB calls, no side
 * effects — so they are safe to call from both React render cycles and inside
 * active Dexie transactions.
 *
 * @see TradePromotionService — service-layer enforcement (T02)
 */

import type { TradeSetupDraft } from '@/types/transient/TradeDraft';

/**
 * Finite typed set of required planning fields that gate trade setup draft
 * promotion. Each member corresponds to a field on TradeSetupDraft that must
 * be present (not undefined) before promotion is permitted.
 *
 * Using a string literal union rather than free-form strings ensures that
 * service error messages and UI callouts are driven by the same type and
 * cannot drift independently.
 */
export type PromotionReadinessField = 'accountId' | 'strategySnapshot' | 'playbookSnapshot';

/**
 * Result returned by the promotion readiness validator.
 *
 * - `isReady`: true when all required planning fields are present; false when
 *   one or more are missing.
 * - `missingFields`: always an array — empty when the draft is ready, populated
 *   with the missing field keys otherwise. Consumers may safely iterate without
 *   a null/undefined guard.
 */
export interface PromotionReadinessResult {
  isReady: boolean;
  missingFields: PromotionReadinessField[];
}

/**
 * Validate whether a TradeSetupDraft satisfies all required planning fields
 * for promotion to a persistent Trade.
 *
 * Required fields checked:
 *   - `accountId`         — must be a non-empty string (set by account picker)
 *   - `strategySnapshot`  — snapshot artifact must be present (ID-only insufficient)
 *   - `playbookSnapshot`  — snapshot artifact must be present (ID-only insufficient)
 *
 * `direction` is explicitly excluded — it is validated by TradePromotionService
 * as part of the execution event payload, not here.
 *
 * Algorithm:
 *   1. Collect missing field keys into an array.
 *   2. Return `{ isReady: missing.length === 0, missingFields: missing }`.
 *
 * `accountId` presence semantics: any non-empty string is valid. Trim
 * validation is not applied — accountId is always a structured UUID set by
 * the account picker, matching current TradePromotionService behavior.
 *
 * @param draft - The TradeSetupDraft to validate.
 * @returns PromotionReadinessResult with `isReady` and `missingFields` (always
 *          an array — empty when ready, populated when not).
 */
export function checkPromotionReadiness(draft: TradeSetupDraft): PromotionReadinessResult {
  const missing: PromotionReadinessField[] = [];
  if (!draft.accountId) missing.push('accountId');
  if (!draft.phase1.strategySnapshot) missing.push('strategySnapshot');
  if (!draft.phase1.playbookSnapshot) missing.push('playbookSnapshot');
  return { isReady: missing.length === 0, missingFields: missing };
}
